/**
 * AiClusterInsight + AiSimilarRecord + AiResolutionTimeBadge — W3 Phase 2
 * components powering AI-201, AI-204, AI-205, AI-203 (resolution time).
 *
 * All three call mock-api endpoints that wrap embed.mjs inline-clustering /
 * inline-similarity. The endpoints embed the supplied corpus inline so they
 * work the moment data arrives — no backfill required.
 */

import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import styles from './AiClusterInsight.module.css';
import { buildHeaders, apiUrl } from '../../api/client';

// ---------------------------------------------------------------------------
//  AiClusterInsight  —  AI-201 (CAPA root-cause clustering) + AI-205 (risk)
// ---------------------------------------------------------------------------

export interface ClusterItem {
  id: string;
  text: string;
  href?: string;
  label?: string;
}

interface ClusterResp {
  advisory_only: true;
  clusters: Array<{ id: string; members: string[]; seedText: string; avgSimilarity: number }>;
  outliers: string[];
  embeddingModel: string | null;
}

export interface AiClusterInsightProps {
  entityKind: string;
  items: ClusterItem[];
  minSimilarity?: number;
  /** Label rendered above the chip — e.g. "Deviation patterns" */
  title?: string;
  className?: string;
}

export function AiClusterInsight({
  entityKind, items, minSimilarity = 0.6, title, className,
}: AiClusterInsightProps) {
  const [state, setState] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; data?: ClusterResp; error?: string }>({
    status: 'idle',
  });

  // Stable cache key by sorted item IDs.
  const cacheKey = items.map((i) => i.id).sort().join(',');

  useEffect(() => {
    if (items.length < 2) {
      setState({ status: 'ready', data: { advisory_only: true, clusters: [], outliers: items.map((i) => i.id), embeddingModel: null } });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetch(apiUrl('/api/v1/ai/cluster'), {
      method: 'POST',
      headers: buildHeaders({ json: true }),
      body: JSON.stringify({
        entityKind,
        items: items.map((i) => ({ id: i.id, text: i.text })),
        minSimilarity,
      }),
    })
      .then((r) => r.json())
      .then((d: ClusterResp) => { if (!cancelled) setState({ status: 'ready', data: d }); })
      .catch((err) => { if (!cancelled) setState({ status: 'error', error: (err as Error).message }); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, minSimilarity, entityKind]);

  const data = state.data;
  const itemById = new Map(items.map((i) => [i.id, i]));

  return (
    <aside
      role="complementary"
      aria-label={`AI clustering — ${entityKind}`}
      className={clsx(styles.cluster, className)}
      data-entity-kind={entityKind}
    >
      <header className={styles.head}>
        <span className={styles.icon} aria-hidden>✦</span>
        <span className={styles.title}>{title || `AI sees ${data?.clusters.length ?? '…'} patterns`}</span>
        {data?.embeddingModel && (
          <span className={styles.modelBadge}>Cloud AI</span>
        )}
      </header>

      {state.status === 'loading' && <p className={styles.muted}>Computing clusters…</p>}
      {state.status === 'error' && <p className={styles.error} role="alert">Cluster failed: {state.error}</p>}
      {state.status === 'ready' && data && (
        <>
          {data.clusters.length === 0 ? (
            <p className={styles.muted}>
              {items.length < 2
                ? 'Need at least 2 records to find clusters.'
                : 'No semantic clusters above threshold — every record is distinct.'}
            </p>
          ) : (
            <ul className={styles.clusterList}>
              {data.clusters.map((c) => (
                <li key={c.id} className={styles.clusterRow}>
                  <span className={styles.clusterSeed}>“{c.seedText}…”</span>
                  <span className={styles.clusterSim}>{Math.round(c.avgSimilarity * 100)}%</span>
                  <ul className={styles.memberList}>
                    {c.members.map((id) => {
                      const it = itemById.get(id);
                      const label = it?.label || id;
                      return (
                        <li key={id} className={styles.memberChip}>
                          {it?.href ? <a href={it.href}>{label}</a> : <span>{label}</span>}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
          {data.outliers.length > 0 && (
            <p className={styles.muted}>
              <strong>{data.outliers.length}</strong> records didn’t cluster.
            </p>
          )}
        </>
      )}
      <p className={styles.disclaimer}>AI advisory only — verify against source records before acting.</p>
    </aside>
  );
}

// ---------------------------------------------------------------------------
//  AiSimilarRecord  —  AI-204 (OOS ↔ Deviation linker)
// ---------------------------------------------------------------------------

interface SimilarResp {
  advisory_only: true;
  similar: Array<{ id: string; similarity: number; snippet: string }>;
  embeddingModel: string | null;
}

export interface AiSimilarRecordProps {
  queryText: string;
  candidates: Array<{ id: string; text: string; href?: string; label?: string }>;
  entityKind: string;
  /** What to call this in the UI — e.g. "Possible linked DEVs" */
  title?: string;
  limit?: number;
  minSimilarity?: number;
  className?: string;
}

export function AiSimilarRecord({
  queryText, candidates, entityKind, title, limit = 3, minSimilarity = 0.55, className,
}: AiSimilarRecordProps) {
  const [state, setState] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; data?: SimilarResp; error?: string }>({
    status: 'idle',
  });

  const cacheKey = `${queryText}:${candidates.map((c) => c.id).sort().join(',')}`;

  useEffect(() => {
    if (!queryText || candidates.length === 0) {
      setState({ status: 'ready', data: { advisory_only: true, similar: [], embeddingModel: null } });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetch(apiUrl('/api/v1/ai/similar-records'), {
      method: 'POST',
      headers: buildHeaders({ json: true }),
      body: JSON.stringify({
        queryText,
        candidates: candidates.map((c) => ({ id: c.id, text: c.text })),
        entityKind,
        limit,
        minSimilarity,
      }),
    })
      .then((r) => r.json())
      .then((d: SimilarResp) => { if (!cancelled) setState({ status: 'ready', data: d }); })
      .catch((err) => { if (!cancelled) setState({ status: 'error', error: (err as Error).message }); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const data = state.data;
  const byId = new Map(candidates.map((c) => [c.id, c]));

  return (
    <aside role="complementary" aria-label={`AI similar — ${entityKind}`} className={clsx(styles.similar, className)}>
      <header className={styles.head}>
        <span className={styles.icon} aria-hidden>✦</span>
        <span className={styles.title}>{title || `Possible ${entityKind} matches`}</span>
        {data?.embeddingModel && (
          <span className={styles.modelBadge}>Cloud AI</span>
        )}
      </header>

      {state.status === 'loading' && <p className={styles.muted}>Searching…</p>}
      {state.status === 'error' && <p className={styles.error} role="alert">Search failed: {state.error}</p>}
      {state.status === 'ready' && data && (
        <>
          {data.similar.length === 0 ? (
            <p className={styles.muted}>No semantically similar {entityKind} records found.</p>
          ) : (
            <ul className={styles.similarList}>
              {data.similar.map((m) => {
                const cand = byId.get(m.id);
                return (
                  <li key={m.id} className={styles.similarRow}>
                    <span className={styles.similarSim}>{Math.round(m.similarity * 100)}%</span>
                    {cand?.href
                      ? <a href={cand.href} className={styles.similarLink}>{cand.label || m.id}</a>
                      : <span className={styles.similarLink}>{cand?.label || m.id}</span>}
                    <span className={styles.similarSnippet}>{m.snippet}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
      <p className={styles.disclaimer}>AI advisory only — verify the linkage before recording it.</p>
    </aside>
  );
}

// ---------------------------------------------------------------------------
//  AiResolutionTimeBadge  —  AI-203 (resolution-time prediction)
// ---------------------------------------------------------------------------

export interface AiResolutionTimeBadgeProps {
  recordKind: string;
  recordId: string;
  context: string;
  /** Optional explicit days target — when present, the badge colours by SLA delta. */
  slaDays?: number;
  className?: string;
}

interface ResolutionResp {
  response: {
    advisory_only: true;
    payload: { predictedDays?: number; summary?: string; concerns?: string[] };
  };
  invocationId: string;
}

export function AiResolutionTimeBadge({ recordKind, recordId, context, slaDays, className }: AiResolutionTimeBadgeProps) {
  const [state, setState] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; days?: number; reason?: string }>({
    status: 'idle',
  });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetch(apiUrl('/api/v1/ai/advisory'), {
      method: 'POST',
      headers: buildHeaders({ json: true }),
      body: JSON.stringify({
        surface: 'oos.resolution-time',
        prompt:
          `Predict the resolution time (in calendar days) for the following ${recordKind} record. ` +
          `Context: ${context}. ` +
          `Reply with JSON {predictedDays: number, summary: string}. ` +
          `Be conservative — pharma resolution typically takes 5–30 days.`,
        cacheKey: `resolution-time:${recordKind}:${recordId}`,
        jsonSchema: {
          type: 'object',
          properties: {
            predictedDays: { type: 'number' },
            summary:       { type: 'string' },
          },
          required: ['predictedDays'],
        },
      }),
    })
      .then((r) => r.json())
      .then((d: ResolutionResp) => {
        if (cancelled) return;
        const p = d?.response?.payload;
        if (typeof p?.predictedDays === 'number') {
          setState({ status: 'ready', days: p.predictedDays, reason: p.summary });
        } else {
          setState({ status: 'error' });
        }
      })
      .catch(() => { if (!cancelled) setState({ status: 'error' }); });
    return () => { cancelled = true; };
  }, [recordKind, recordId, context]);

  if (state.status === 'loading') return <span className={clsx(styles.badge, styles.badgeLoading, className)}>…</span>;
  if (state.status !== 'ready' || state.days == null) return null;

  const tone = (() => {
    if (slaDays == null) return 'neutral';
    if (state.days <= slaDays) return 'ok';
    if (state.days <= slaDays * 1.5) return 'warn';
    return 'danger';
  })();

  return (
    <span
      className={clsx(styles.badge, styles[`badge_${tone}`], className)}
      title={state.reason || `AI prediction for ${recordId}`}
    >
      ✦ {state.days}d
    </span>
  );
}
